import ConnectableObject from "./ConnectableObject";
import { copyAllProperties } from "./utils";
import ChannelTreeViewModel from "../modelTreeViews/ChannelTreeviewModel";
import type Scene from "./Scene";
import BaseModel from "./BaseModel";
import Channel, { ButtonSide, SinglePortSide } from "./Channel";
import { areChannelTypesCompatible, ChannelType, channelTypeInfo, electricalChannelType } from "./ChannelType";
import type Button from "./Button";
import type Page from "./Page";
import Connection, { ConnectionType } from "./Connection";
import type Multiplexer from "./Multiplexer";

import Logger from "../Logger";
const logger = new Logger();

export class KnxSceneNumberProperty {
  id: string = "";
  instanceId: string = "";
  moduleId: string = "";
}

//////////////////////////////////////////////////
import ObjectFactory from "./ObjectFactory";
import { PortInfo } from "./PortInfos";
import BaseObject from "./BaseObject";
import { RockerConfig } from "./KnxRules";
import Room from "./Room";
import { TAG_TX } from "./Constants";

ObjectFactory.registerClass("ButtonChannel", (data?: any, parent?: BaseModel) => {
  return new ButtonChannel(data, parent);
});
//////////////////////////////////////////////////

export default class ButtonChannel extends Channel {
  public get className(): string {
    return "ButtonChannel";
  }
  constructor(data?: any, parent?: BaseModel) {
    super(data, parent);
    copyAllProperties(data, this);
    this.singlePortSideN = SinglePortSide.leftRightAuto;

    this.treeViewModel = new ChannelTreeViewModel(this);
  }

  public get isButtonChannel(): boolean {
    return true;
  }

  public get isActorChannel(): boolean {
    if (this.tag.startsWith("A")) {
      logger.log("isActorChannel: ButtonChannel is an ActorChannel", this.tag); // no itx
      return true;
    }
    return false;
  }

  /** Adopts a supported consumer type only while the button function is unused. */
  override autoSetChannelType(channelType: ChannelType): boolean {
    channelType = electricalChannelType(channelType);
    if (this.channelType === channelType) return true;
    // Fixed sensor/special channels and actor outputs must never become ordinary keys.
    if (
      this.tag.startsWith("TX_") ||
      this.knxChannel?.tag.startsWith("TX_") ||
      this.isActorChannel ||
      this.connections.size > 0
    )
      return false;
    if (channelType === ChannelType.TemperatureMeasurement || channelType === ChannelType.TemperatureSetpoint) {
      channelType = ChannelType.Heating;
    }
    if (this.channelType === channelType) return true;
    // Match OneButton's restriction for a combined rocker.
    if (channelType === ChannelType.Scene && this.rockerConfig === RockerConfig.rocker) return false;

    const supported = this.allowedChannelTypes?.some(
      (option) =>
        option.channelType === channelType ||
        (option.channelType === ChannelType.GenericValue && channelType === ChannelType.Switch),
    );
    if (!supported || !this.canControlChannelType) return false;

    this.channelType = channelType;
    // Use the same parameter writer as an explicit type choice in OneButton.
    this.setKnxChannelTypePropertyValue(channelType, false);
    this.raiseOnChange("channelType");
    return true;
  }

  getChannelType(): ChannelType {
    //
    // Channels of Buttons without a template that are not yet connected are allways generic
    //
    if (this.parent) {
      if (!(this.parent as Button).template) {
        if (!this.connections || this.connections?.size == 0) {
          return ChannelType.Generic;
        }
      }
    }
    return super.getChannelType();
  }

  //////////////////////////////
  // persistent properties

  private _ctu?: boolean = false;
  public get channelTypeIsSetByUser(): boolean {
    return this._ctu == true;
  }
  public set channelTypeIsSetByUser(value: boolean) {
    this._ctu = value;
  }

  //
  // set defined to avoid crash in old project loading ......
  //
  set buttonSide(newValue: ButtonSide) {
    this.beforeChange("buttonSide");
    this.changed("buttonSide");
  }

  get buttonSide(): ButtonSide {
    let t = this.tag;
    // remove longPress and extraLongPress from tag
    // for Left / Right analysis
    //
    if (t.endsWith("_xl")) {
      t = t.substring(0, t.length - 3);
    } else if (t.endsWith("_l") || t.endsWith("_x")) {
      t = t.substring(0, t.length - 2);
    }

    if (t.endsWith("R")) {
      if (this.isVirtualRocker) {
        return ButtonSide.hidden;
      } else {
        return ButtonSide.right;
      }
    } else if (t.endsWith("L")) {
      if (this.isVirtualRocker) {
        return ButtonSide.both;
      } else {
        return ButtonSide.left;
      }
    } else {
      return ButtonSide.both;
    }
  }

  // -----------------------------------------------------------------------------
  // isVirtualRocker
  // -----------------------------------------------------------------------------
  _isVirtualRocker?: boolean = false;

  get isVirtualRocker(): boolean {
    return this._isVirtualRocker || false;
  }

  set isVirtualRocker(newValue: boolean) {
    this.beforeChange("isVirtualRocker");
    this._isVirtualRocker = newValue;

    this.changed("isVirtualRocker");
  }

  // -----------------------------------------------------------------------------

  public get buttonNumber(): number {
    const s = this.tag.substring(1, 3);
    return parseInt(s);
  }

  public set buttonNumber(value: number) {
    const postfix = this.tag.substring(3);
    this.tag = "T" + ("0" + value).slice(-2) + postfix;
  }

  static buildTag(buttonNumber: number, buttonSide: ButtonSide): string {
    let postfix = "";
    if (buttonSide == ButtonSide.right) {
      postfix = "R";
    } else if (buttonSide == ButtonSide.left) {
      postfix = "L";
    }
    return "T" + ("0" + buttonNumber).slice(-2) + postfix;
  }

  // -----------------------------------------------------------------------------
  // knxButtonSceneNumberProperty
  // -----------------------------------------------------------------------------
  _knxButtonSceneNumberProperty: KnxSceneNumberProperty | undefined;
  get knxButtonSceneNumberProperty(): KnxSceneNumberProperty | undefined {
    return this._knxButtonSceneNumberProperty;
  }

  set knxButtonSceneNumberProperty(newValue: KnxSceneNumberProperty | undefined) {
    if (this._knxButtonSceneNumberProperty === newValue) return;
    this.beforeChange("knxButtonSceneNumberProperty");
    this._knxButtonSceneNumberProperty = newValue;
    this.changed("knxButtonSceneNumberProperty");
  }

  // -----------------------------------------------------------------------------

  getPersistentProperties(): string[] {
    return [
      //   "channelType",
      //   "singlePortSide",
      //   "signals",
      //   "knxChannelId",
      //   "mappedId",
      //   "index",
      //   "isRequestChannel",
      //   "knxSceneInfos",
      "isVirtualRocker", // no itx
      "buttonSide", // no itx
      "knxButtonSceneNumberProperty", // no itx
      "_ctu", // no itx
      ...super.getPersistentProperties(),
    ];
  }
  //////////////////////////////

  getConnectedScene(): Scene | undefined {
    let res: Scene | undefined = undefined;

    let requestChannel: Channel | undefined = this;
    if (!requestChannel.isRequestChannel) {
      requestChannel = requestChannel.mappedChannel;
    }

    if (requestChannel) {
      requestChannel.connections.forEach((connection) => {
        const partner = connection.getPartner(requestChannel!);
        if (partner?.className == "Scene") {
          res = partner as Scene;
        }
      });
    }

    return res;
  }

  getConnectedMultiplexer(): Multiplexer | undefined {
    return this.getConnectedObject("Multiplexer") as Multiplexer; // no itx
  }

  otherSideButtonOfRocker(): ButtonChannel | undefined {
    const device = this.parent as Button;

    if (device) {
      if (this.tag.endsWith("L")) {
        return device.buttonChannels.getItemByTag(this.tag.replace("L", "R"));
      } else if (this.tag.endsWith("R")) {
        return device.buttonChannels.getItemByTag(this.tag.replace("R", "L"));
      }
    }
    return undefined;
  }

  delete(): Array<BaseObject> {
    this.isDeleting = true;

    let res = new Array<BaseObject>();
    // delete all child signals
    if (this.signals) {
      res = res.concat(this.signals.deleteAllItems());
    }

    const button = this.parent as Button;
    if (button) {
      //   logger.log(`ondelete buttonChannel` + buttonChannel);

      //
      // hiermit geben wird dem Customizing eine Chance auf das löschen des ButtonChannesl zu reagieren

      // MWE 14.2.2025
      // (zumindest für jetzt hier) wieder disabled da die Komplexität zu hoch wird
      // zb wird beim Mapping ein Channel gelöscht,
      // dabei soll dann aber nicht auch noch die ChannelTypeProperty geändert werden
      // das für ggfl zu EnlosSchleifen führen und allgem zu kaum mehr nachvollziehbaren Effekten
      //

      // this.channelType = ChannelType.Disabled;

      const delConections = this.connections.deleteAllItems();
      res = res.concat(delConections);

      res = res.concat(super.delete());

      ////////////////////////////////////////////

      // const buttonChannel2 = this.otherSideButtonOfRocker();
      // if (buttonChannel2) {
      //   //
      //   // Achtung 2. Button tag hier entfernen damit man sich nicht endlos gegenseitig aufruft!!!
      //   //
      //   buttonChannel2.tag = "";
      //   res = res.concat(buttonChannel2.delete());
      // }
    }
    // this.mappedChannel = undefined; // mwe 28.9.23 keep mappedChannel for undo !

    /// delete actor if autocreated and channelSize==0

    return res;
  }

  assetIsActorChannel(): boolean {
    logger.error("Channel is not a ActorChannel");
    return false;
  }

  assetIsButtonChannel(): boolean {
    return true;
  }

  getIconUrl(): string | undefined {
    return state.hostBaseUrl + "images/buttons/touch-6-1ch.png";
  }

  setChannelType(newValue: ChannelType) {
    //
    // Ajust the channelType if the channel is is a statusChannel
    //

    if (this.isRequestChannel && this.knxChannel?.isStatusChannel) {
      switch (newValue) {
        case ChannelType.Switch:
          newValue = ChannelType.StatusDisplaySwitch;
      }
    }

    const lastValue = this._channelType;
    this._channelType = newValue;

    if (state.isLoadingFromEstadaStorage) {
      return;
    }

    if (
      this.isRequestChannel &&
      this.channelType != ChannelType.Unknown &&
      this.channelType != ChannelType.General &&
      this.channelType != ChannelType.Environment &&
      this.channelType != ChannelType.TemperatureMeasurement
    ) {
      if (lastValue != newValue) {
        if (
          ![
            ChannelType.Unknown,
            ChannelType.General,
            ChannelType.Environment,
            ChannelType.TemperatureMeasurement,
          ].includes(newValue)
        ) {
          this.applyChannelTypeProperty();
          this.executeSetChannelTypeCustomizingScript(lastValue);
        }
      }
    }
  }

  applyConsumerChannelType() {
    if (!this.isGroupButtonChannel) {
      let requestChannel: ButtonChannel | undefined = this;
      if (!this.isRequestChannel) {
        requestChannel = this.requestChannel as ButtonChannel;
      }
      const consumer = requestChannel.getConnectedConsumer();
      if (consumer) {
        requestChannel.channelType = consumer.channelType;
      }
    }
  }

  applyChannelTypeProperty() {
    let requestChannel: ButtonChannel | undefined = this;
    if (!this.isRequestChannel) {
      requestChannel = this.requestChannel as ButtonChannel;
    }

    if (requestChannel) {
      requestChannel.setKnxChannelTypePropertyValue(requestChannel.channelType, false);
    }
  }

  get mayCallSetRockerConfig(): boolean {
    return (
      !this.tag.startsWith("A") && // ActorChannel
      !this.tag.startsWith("S") && // StatusChannel
      !this.tag.startsWith(TAG_TX) && // TemperatureChannel
      !this.tag.endsWith("_l") && // long Press
      !this.tag.endsWith("_x") // extra long Press
    );
  }

  executeSetChannelTypeCustomizingScript(lastChannelType: ChannelType) {
    const button = this.parent as Button;

    if (button?.template) {
      const rules = button.template.knxProductData.currentRules;
      if (rules) {
        if (
          (this.channelType == ChannelType.Disabled || lastChannelType == ChannelType.Disabled) &&
          this.buttonSide == ButtonSide.both
        ) {
          if (this.mayCallSetRockerConfig) {
            rules.setRockerConfig(button, this);
          }
        }

        //
        // if setChannelType retuns a string, it is an error message
        //
        const res = rules.setChannelType(button, this);

        //
        // flagErrors calls raiseOnChange
        //
        this.flagErrors({ channelTypeError: res }, res && typeof res == "string");
      }
    }
  }

  executeSetRockerCustomizingScript() {
    const button = this.parent as Button;
    if (button?.template && this.mayCallSetRockerConfig) {
      const rules = button.template.knxProductData.currentRules;
      if (rules) {
        const res = rules.setRockerConfig(button, this);
        const oldValues = this.errors?.rockerTypeError;

        this.flagErrors({ rockerTypeError: res }, res && typeof res == "string");

        if (res != oldValues) {
          this.raiseOnChange("errors");
        }
      }
    }
  }

  applyScripts() {
    // MWE 7.3.2025:
    //
    // Die Rocker-Config wird NIE mit dem rechten Button-Teil gesetzte
    // Ansonsten setzt man die Rocker-Config 2 mal.
    // Dabei ist dann uU. der rechte Button Disabled und setzt dann dadurc den gesamten Rocker auf Disabled
    //
    //
    if (!this.tag.endsWith("R")) {
      this.executeSetRockerCustomizingScript();
    }

    this.executeSetChannelTypeCustomizingScript(ChannelType.Unknown);
  }

  canBeDeletedInteractive(): boolean {
    if (this.mappedChannel) {
      return false;
    }

    //
    // Einzelne ButtonChannels dürfen nicht gelöscht werden,
    // wenn sie Teil eines C1-Projekts sind,
    // da wir dort lange mit Buttons ohne Geräteauswahl arbeiten.
    //
    // Die Button werden dann durch die einstellungn des Button konfiguriert.
    //
    if (state.projectMetadata.isC1Project) {
      if (!this.parentDevice?.template) {
        return false;
      }
    }

    return true;
  }

  getPortPosition(document: Document, mainPageId: string, connection: Connection): PortInfo | undefined {
    return super.getPortPosition(document, mainPageId, connection);
  }

  getConnectedActorChannels(): Array<Channel> {
    const res = new Array<Channel>();

    this.getConnectedConsumersOrMultiplexerforButton().forEach((consumer) => {
      const channel = consumer.getConnectedChannel();
      if (channel) {
        res.push(channel);
      }
    });

    return res;
  }

  get isGroupButtonChannel(): boolean {
    if (this.channelType != ChannelType.Scene) {
      if (this.connections.size > 1) {
        return true;
      }
    }

    return false;
  }

  channelTypeFromPropertyOrKnxChannel(): ChannelType {
    let xChannelType = this.getKnxChannelTypePropertyValue();
    if (xChannelType == ChannelType.Unknown) {
      //
      // in den Parametern ist ein unbekannter / "ungemappter" ChannelType eingestellt
      //
      // In dem Fall nehen wir den erkannten ChannelType aufgrund der Signal /Kanal-Erkennung
      // als faktisches Ergebnis des vom Benutzer eingestellten Property-Values
      //
      if (this.knxChannel) {
        xChannelType = this.knxChannel.channelType;
      }
    }
    return xChannelType;
  }

  isParameterChannelTypeDifferent(): boolean {
    const xChannelType = this.channelTypeFromPropertyOrKnxChannel();
    if (this.channelType != xChannelType && this.channelType != ChannelType.Generic) {
      return true;
    }
    return false;
  }

  isParameterChannelTypeCompatible(): boolean {
    const channelType1 = this.channelTypeFromPropertyOrKnxChannel();
    return areChannelTypesCompatible(channelType1, this.channelType, this.parent as Button);
  }

  get rockerConfig(): RockerConfig {
    const char1 = this.tag.substring(0, 1);
    const char2 = this.tag.substring(1, 2);

    if (char1 != "T" || char2 > "9") {
      //
      // normalerweise sind die Buttontags T01 bis T99
      //
      // Tasten mit Tags wie TA1, TB4 etc verwenden wir für Sonderfunktionen
      // wie Ausgänge von Präsenzmeldern etc.
      //
      // Für die unterstützen wir keine ROCKER
      //
      return RockerConfig.none;
    }

    return (this.parent as Button)?.getRockerConfig(this.buttonNumber);
  }

  get supportsRockerConfig(): boolean {
    const char1 = this.tag.substring(0, 1);
    const char2 = this.tag.substring(1, 2);
    if (char1 != "T" || char2 > "9") {
      return false;
    }

    return this.parentDevice?.baseProps.buttonBaseProps?.supportsSplitRocker == true;
  }

  set rockerConfig(newValue: RockerConfig) {
    const char2 = this.tag.substring(1, 2);
    if (char2 > "9") {
      //
      // Tasten mit Tags wie TA1, TB4 etc verwenden wir für Sonderfunktionen
      // wie Ausgänge von Präsenzmeldern etc.
      //
      // Für die unterstützen wir keine ROCKER
      //
      return;
    }

    (this.parent as Button)?.setRockerConfig(this.buttonNumber, newValue);
  }

  get rightButtonOfVirtualRocker(): ButtonChannel | undefined {
    if (this.isVirtualRocker) {
      const button = this.parent as Button;
      if (button) {
        const tag = this.tag.substring(0, 3) + "R";
        if (this.isRequestChannel) {
          return button.buttonChannels.getItemByTag(tag);
        } else {
          return button.knxChannels.getItemByTag(tag) as ButtonChannel;
        }
      }
    }
    return undefined;
  }

  deleteOnlyThisChannel(): Array<BaseObject> {
    const res = super.deleteOnlyThisChannel();

    if (this.buttonSide == ButtonSide.both) {
      const button = this.parent as Button;

      const rules = button?.template?.knxProductData.currentRules;
      if (rules && this.mayCallSetRockerConfig) {
        rules.setRockerConfig(button, this, true);
      }
    }

    return res;
  }

  get room(): Room | undefined {
    return (this.parent as Button)?.room;
  }
} // class ButtonChannel

export function splitRockerToSingleButtons(buttonChannel: ButtonChannel) {
  buttonChannel.rockerConfig = RockerConfig.single;

  const button = buttonChannel.parent as Button;
  const tag = buttonChannel.tag.substring(0, 3);
  if (button) {
    const newButtonChannel = button.createNewRequestChannel();
    newButtonChannel.tag = tag + "R";
    // Start the new right half with the type of the consumer already connected
    // to the rocker instead of an arbitrary hard-coded default.
    const connectedConsumer = buttonChannel.getConnectedConsumer();
    newButtonChannel.channelType = connectedConsumer?.channelType ?? ChannelType.Switch;
  }

  buttonChannel.knxChannel = undefined; // needs to by matched new
  buttonChannel.tag = tag + "L";
}

export function mergeSingleButtonsToRocker(buttonChannel: ButtonChannel) {
  //
  // Assert this is a Left Button Channel
  //

  buttonChannel.rockerConfig = RockerConfig.rocker;

  if (buttonChannel.buttonSide != ButtonSide.left) {
    logger.error("invalid buttonSide 1");
    return;
  }

  const button = buttonChannel.parent as Button;
  if (button) {
    const rightButtonChannelTag = buttonChannel.tag.substring(0, 3) + "R";
    const rightButtonChannel = button.buttonChannels.getItemByTag(rightButtonChannelTag);

    //
    // Setting the channelType of the left / now both button
    // to force the channelType of the right button of a virtual rocker
    // to the same value
    //
    rightButtonChannel?.setKnxChannelTypePropertyValue(buttonChannel.channelType, true);

    // if (buttonChannel.isVirtualRocker) {
    //   //
    //   // For a virtual Rocker we dont change Tags !!
    //   // We only disconneect the right button
    //   //
    //   if (rightButtonChannel) {
    //     const connections = rightButtonChannel.connections.asArray();
    //     connections.forEach((connection) => {
    //       connection.delete();
    //     });
    //   }

    //   return;
    // }

    if (rightButtonChannel && rightButtonChannel?.buttonSide == ButtonSide.right) {
      rightButtonChannel.knxChannel = undefined; // deleted => no longer needed
      buttonChannel.knxChannel = undefined; // needs to by matched new

      if (rightButtonChannel.connections.size > 0) {
        //
        // right ButtonChannel is connected.
        //
        //
        // MWE TODO, 15.5.2025
        // look if the next button is free,
        // if so move the connections to the next button
        //
      }

      button.channels.remove(rightButtonChannel);
      rightButtonChannel.delete();

      buttonChannel.tag = buttonChannel.tag.substring(0, 3);
    }
  }
}
